/*
 * Upstreet additions on top of Dawn:
 * - product sliders: arrows on both sides, looping, optional autoplay
 * - <upstreet-size-guide>: opens the size chart in a dialog
 */

(function () {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Pauses while a shopper is using the strip, then carries on.
  // Phones send fake mouse and focus events after a tap that never "leave",
  // so only a real mouse hover or keyboard focus counts as a lasting pause.
  function autoAdvance(container, seconds, step) {
    if (!seconds || reduceMotion) return;
    let paused = false;
    let inView = false;
    let resumeTimer = null;
    const pause = () => {
      paused = true;
      clearTimeout(resumeTimer);
    };
    const resumeSoon = () => {
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(() => {
        paused = false;
      }, seconds * 1000);
    };

    container.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse') pause();
    });
    container.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'mouse') resumeSoon();
    });
    container.addEventListener('touchstart', pause, { passive: true });
    container.addEventListener('touchend', resumeSoon, { passive: true });
    container.addEventListener('touchcancel', resumeSoon, { passive: true });
    container.addEventListener('focusin', (event) => {
      if (event.target.matches(':focus-visible')) pause();
    });
    container.addEventListener('focusout', resumeSoon);
    new IntersectionObserver(
      (entries) => {
        inView = entries[0].isIntersecting;
      },
      { threshold: 0.3 }
    ).observe(container);

    setInterval(() => {
      if (paused || !inView || document.hidden) return;
      step();
    }, seconds * 1000);
  }

  function setUpSlider(component) {
    if (component.dataset.upstreetReady) return;
    const slider = component.querySelector('[id^="Slider-"]');
    const prevButton = component.querySelector('button[name="previous"]');
    const nextButton = component.querySelector('button[name="next"]');
    if (!slider || !prevButton || !nextButton) return;
    component.dataset.upstreetReady = 'true';

    const atStart = () => slider.scrollLeft <= 4;
    const atEnd = () => slider.scrollLeft + slider.clientWidth >= slider.scrollWidth - 4;

    // Loop instead of stopping at either end, so both arrows always work.
    component.enableSliderLooping = true;
    prevButton.removeAttribute('disabled');
    nextButton.removeAttribute('disabled');
    component.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest('button[name="previous"], button[name="next"]');
        if (!button || !component.contains(button)) return;
        const wrap = button.name === 'next' ? atEnd() : atStart();
        if (!wrap) return;
        event.preventDefault();
        event.stopPropagation();
        slider.scrollTo({ left: button.name === 'next' ? 0 : slider.scrollWidth });
      },
      true
    );

    // Centre the side arrows on the product photos rather than the whole card.
    const placeArrows = () => {
      const media = slider.querySelector('.card__inner');
      if (!media || media.offsetHeight === 0) return;
      const top = media.getBoundingClientRect().top - component.getBoundingClientRect().top + media.offsetHeight / 2;
      component.style.setProperty('--upstreet-arrow-top', `${Math.round(top)}px`);
    };
    placeArrows();
    new ResizeObserver(placeArrows).observe(slider);

    autoAdvance(component, parseInt(component.dataset.autoplay, 10), () => nextButton.click());
  }

  // Plain swipe strips (e.g. reviews on phones): move one card at a time, loop at the end.
  function setUpAutoScroll(list) {
    if (list.dataset.upstreetReady) return;
    list.dataset.upstreetReady = 'true';
    autoAdvance(list, parseInt(list.dataset.upstreetAutoscroll, 10), () => {
      if (list.scrollWidth <= list.clientWidth + 4) return;
      const card = list.firstElementChild;
      const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
      const atEnd = list.scrollLeft + list.clientWidth >= list.scrollWidth - 4;
      list.scrollTo({ left: atEnd ? 0 : list.scrollLeft + card.offsetWidth + gap, behavior: 'smooth' });
    });
  }

  function setUp(root) {
    root.querySelectorAll('slider-component.upstreet-slider').forEach(setUpSlider);
    root.querySelectorAll('[data-upstreet-autoscroll]').forEach(setUpAutoScroll);
  }

  // Slider extras aren't needed for the first paint: set them up once the browser is idle.
  const whenIdle = (callback) =>
    'requestIdleCallback' in window ? window.requestIdleCallback(callback, { timeout: 2000 }) : setTimeout(callback, 300);
  customElements.whenDefined('slider-component').then(() => {
    whenIdle(() => setUp(document));
    document.addEventListener('shopify:section:load', (event) => setUp(event.target));
  });

  class UpstreetSizeGuide extends HTMLElement {
    connectedCallback() {
      this.opener = this.querySelector('[data-size-guide-open]');
      this.dialog = this.querySelector('dialog');
      if (!this.opener || !this.dialog || typeof this.dialog.showModal !== 'function') return;

      // Without dialog support the link simply opens the chart image.
      this.opener.addEventListener('click', (event) => {
        event.preventDefault();
        this.dialog.showModal();
      });
      this.dialog.querySelector('[data-size-guide-close]')?.addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });
    }
  }

  if (!customElements.get('upstreet-size-guide')) {
    customElements.define('upstreet-size-guide', UpstreetSizeGuide);
  }

  // Strip arrows (WhatsApp reviews, customer videos): one card per click, looping.
  document.addEventListener('click', (event) => {
    const arrow = event.target.closest('[data-upstreet-strip-prev], [data-upstreet-strip-next]');
    if (!arrow) return;
    const list = arrow.closest('[data-upstreet-strip]')?.querySelector('.upstreet-strip__list');
    if (!list || !list.firstElementChild) return;
    const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
    const step = list.firstElementChild.offsetWidth + gap;
    const forward = arrow.hasAttribute('data-upstreet-strip-next');
    const atEnd = list.scrollLeft + list.clientWidth >= list.scrollWidth - 4;
    const atStart = list.scrollLeft <= 4;
    let left = list.scrollLeft + (forward ? step : -step);
    if (forward && atEnd) left = 0;
    if (!forward && atStart) left = list.scrollWidth;
    list.scrollTo({ left, behavior: 'smooth' });
  });

  // Tap a WhatsApp screenshot to see it full size.
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-upstreet-lightbox]');
    if (!link) {
      const closeButton = event.target.closest('[data-upstreet-lightbox-close]');
      if (closeButton) closeButton.closest('dialog')?.close();
      return;
    }
    const dialog = document.querySelector('[data-upstreet-lightbox-dialog]');
    if (!dialog || typeof dialog.showModal !== 'function') return;
    event.preventDefault();
    let image = dialog.querySelector('img');
    if (!image) {
      image = document.createElement('img');
      image.className = 'upstreet-lightbox__image';
      dialog.querySelector('[data-upstreet-lightbox-body]').appendChild(image);
    }
    image.src = link.href;
    image.alt = link.getAttribute('aria-label') || '';
    dialog.showModal();
  });
  document.addEventListener('click', (event) => {
    if (event.target.matches?.('[data-upstreet-lightbox-dialog]')) event.target.close();
  });

  // "Get it by Tue 14 – Wed 15 Oct": counts working days from today.
  class UpstreetDeliveryEstimate extends HTMLElement {
    connectedCallback() {
      const output = this.querySelector('[data-delivery-dates]');
      const minDays = parseInt(this.dataset.minDays, 10);
      const maxDays = parseInt(this.dataset.maxDays, 10);
      if (!output || !minDays || !maxDays) return;
      const saturdayWorking = this.dataset.saturday === 'true';

      const addWorkingDays = (days) => {
        const date = new Date();
        let added = 0;
        while (added < days) {
          date.setDate(date.getDate() + 1);
          const day = date.getDay();
          if (day === 0 || (day === 6 && !saturdayWorking)) continue;
          added += 1;
        }
        return date;
      };
      const first = addWorkingDays(Math.min(minDays, maxDays));
      const last = addWorkingDays(Math.max(minDays, maxDays));
      const dayAndDate = (date) => date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' });
      const full = (date) => date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });

      if (first.toDateString() === last.toDateString()) {
        output.textContent = full(last);
      } else if (first.getMonth() === last.getMonth()) {
        output.textContent = `${dayAndDate(first)} – ${full(last)}`;
      } else {
        output.textContent = `${full(first)} – ${full(last)}`;
      }
    }
  }

  if (!customElements.get('upstreet-delivery-estimate')) {
    customElements.define('upstreet-delivery-estimate', UpstreetDeliveryEstimate);
  }

  // Shopify's preview bar (theme preview only) covers the bottom of the screen; leave room for it.
  function measurePreviewBar() {
    const bar = document.getElementById('PBarNextFrameWrapper') || document.getElementById('preview-bar-iframe');
    const height = bar && bar.getBoundingClientRect().height > 0 ? Math.round(bar.getBoundingClientRect().height) : 0;
    document.documentElement.style.setProperty('--upstreet-preview-bar', `${height}px`);
  }
  window.addEventListener('load', () => setTimeout(measurePreviewBar, 1500));
  window.addEventListener('resize', measurePreviewBar);

  /*
   * Offers: "2 pairs" adds both pairs to the cart in one go. The saving itself is a
   * Shopify automatic discount, so the cart and checkout show it on their own.
   */
  const cartRoutes = window.routes || {};

  function cartDrawer() {
    return document.querySelector('cart-drawer');
  }

  function renderCart(state) {
    const drawer = cartDrawer();
    if (!drawer || !state || !state.sections) return;
    drawer.getSectionsToRender().forEach((section) => {
      const target = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      const html = state.sections[section.id];
      if (!target || !html) return;
      target.innerHTML = drawer.getSectionInnerHTML(html, section.selector);
    });
    drawer.classList.toggle('is-empty', state.item_count === 0);
    drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', drawer.close.bind(drawer));
  }

  async function cartRequest(url, body) {
    const drawer = cartDrawer();
    const sections = drawer ? drawer.getSectionsToRender().map((section) => section.id) : [];
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections, sections_url: window.location.pathname }),
    });
    const state = await response.json();
    if (!response.ok || state.status) throw new Error(state.description || state.message || 'Cart update failed');
    return state;
  }

  // Ticking "2 pairs" shows the size select for the second pair.
  document.addEventListener('change', (event) => {
    const pairs = event.target.closest('[data-pairs]');
    if (!pairs) return;
    const picker = pairs.closest('upstreet-offer-picker');
    picker?.querySelector('[data-second-pair]')?.toggleAttribute('hidden', !pairs.checked);
  });

  // The deal's own "Add 2 pairs to cart" button submits the product form, which the handler below picks up.
  document.addEventListener('click', (event) => {
    const addButton = event.target.closest('[data-offer-add]');
    if (!addButton) return;
    const form = document.getElementById(addButton.dataset.formId);
    if (!form) return;
    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else form.querySelector('[type="submit"]')?.click();
  });

  // "2 pairs": add both pairs in one go, then open the cart.
  document.addEventListener(
    'submit',
    async (event) => {
      const form = event.target;
      if (!form.matches('form[action*="/cart/add"]')) return;
      const productInfo = form.closest('product-info');
      const picker = productInfo?.querySelector('upstreet-offer-picker');
      if (!picker?.querySelector('[data-pairs]:checked')) return;

      event.preventDefault();
      event.stopPropagation();
      const button = form.querySelector('[type="submit"]');
      const firstId = form.querySelector('[name="id"]')?.value;
      const secondId = picker.querySelector('[data-second-variant]')?.value || firstId;
      if (!firstId) return;

      const dealButton = picker.querySelector('[data-offer-add]');
      dealButton?.setAttribute('aria-disabled', 'true');
      dealButton?.classList.add('loading');
      button?.classList.add('loading');
      button?.setAttribute('aria-disabled', 'true');
      button?.querySelector('.loading__spinner')?.classList.remove('hidden');
      try {
        const state = await cartRequest(`${cartRoutes.cart_add_url || '/cart/add'}.js`, {
          items: [
            { id: Number(firstId), quantity: 1 },
            { id: Number(secondId), quantity: 1 },
          ],
        });
        renderCart(state);
        if (pageWantsPrepaid()) await setPrepaid(true);
        cartDrawer()?.open();
      } catch (error) {
        window.alert(error.message);
      } finally {
        dealButton?.classList.remove('loading');
        dealButton?.removeAttribute('aria-disabled');
        button?.classList.remove('loading');
        button?.removeAttribute('aria-disabled');
        button?.querySelector('.loading__spinner')?.classList.add('hidden');
      }
    },
    true
  );

  /*
   * Pay in advance: ticking it puts the pay-in-advance discount codes (Theme settings > Offers) on
   * the cart. A Smart Checkout Rules payment rule hides Cash on Delivery whenever one of them is
   * applied, so the saving and the missing COD always go together. Other codes are never touched.
   */
  const prepaidCodes = (window.upstreetPrepaidCodes || []).map((code) => code.trim().toUpperCase()).filter(Boolean);
  const isPrepaidCode = (code) => prepaidCodes.includes(String(code).toUpperCase());

  // The product page tick box (until the first pair is in the cart, the cart can't hold the codes).
  function pageWantsPrepaid() {
    return Boolean(document.querySelector('[data-payment]:checked'));
  }

  // Shows the choice on the product page; "Buy it now" skips the cart, so it hides while paying in advance.
  function reflectPrepaid(on) {
    document.querySelectorAll('[data-payment]').forEach((input) => {
      input.checked = on;
    });
    document.querySelectorAll('[data-prepaid-details]').forEach((details) => details.toggleAttribute('hidden', !on));
    document.documentElement.classList.toggle('upstreet-prepaid', on);
  }

  // Adds or removes only the pay-in-advance codes (they're only added once the cart has a pair in it).
  async function setPrepaid(on) {
    if (!prepaidCodes.length) return;
    const cart = await fetch(`${cartRoutes.cart_url || '/cart'}.js`).then((response) => response.json());
    if (on && cart.item_count === 0) return;
    const current = (cart.discount_codes || []).map((entry) => entry.code);
    const others = current.filter((code) => !isPrepaidCode(code));
    const wanted = on ? others.concat(prepaidCodes) : others;
    const hasAll = (list, codes) => codes.every((code) => list.some((entry) => entry.toUpperCase() === code.toUpperCase()));
    if (wanted.length === current.length && hasAll(current, wanted)) return;
    const state = await cartRequest(`${cartRoutes.cart_update_url || '/cart/update'}.js`, { discount: wanted.join(',') });
    renderCart(state);
  }

  if (prepaidCodes.length) {
    document.addEventListener('change', (event) => {
      // The product page and cart drawer tick boxes
      const input = event.target.closest('[data-payment], [data-prepaid-toggle]');
      if (!input) return;
      const on = input.checked;
      reflectPrepaid(on);
      setPrepaid(on).catch((error) => console.error('Upstreet pay in advance:', error));
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        // A pair was just added with "Pay in advance" picked on the page: apply the codes now.
        if (event?.source === 'product-form' && pageWantsPrepaid()) {
          setPrepaid(true).catch((error) => console.error('Upstreet pay in advance:', error));
        }
        // The cart was emptied: drop the codes so they can't apply to a later cash-on-delivery order.
        if (event?.source === 'cart-items' && event.cartData?.item_count === 0) {
          setPrepaid(false).catch((error) => console.error('Upstreet pay in advance:', error));
        }
      });
    }

    // A cart from earlier that already pays in advance: show that choice on the product page.
    window.addEventListener('load', () => {
      if (!document.querySelector('[data-payment]')) return;
      if (!document.querySelector('#cart-icon-bubble .cart-count-bubble')) return;
      whenIdle(async () => {
        const cart = await fetch(`${cartRoutes.cart_url || '/cart'}.js`).then((response) => response.json());
        if ((cart.discount_codes || []).some((entry) => isPrepaidCode(entry.code))) reflectPrepaid(true);
      });
    });
  }
})();
